import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import fleetApi from "@/lib/fleetApi";
import { getOrgTimeZone, fmtTime, fmtDayKey, dayKey } from "@/lib/time";
import { pausedDriverIds } from "@/lib/pausedDrivers";
import { AlertTriangle, CalendarClock } from "lucide-react";

const localParts = (iso) => {
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: getOrgTimeZone(), weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
    .formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return { wd: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].indexOf(p.weekday), hm: `${p.hour}:${p.minute}` };
};

// After an import, list trips whose pickup time falls outside every active driver's weekly shift.
export function ImportShiftWarning({ tripIds, days }) {
  const [off, setOff] = useState(null);

  useEffect(() => {
    const ids = new Set(tripIds || []);
    if (!ids.size || !days.length) return;
    Promise.all([Promise.all(days.map((d) => fleetApi.get(`/trips?date=${d}&limit=5000`).then((r) => r.data || []).catch(() => []))), fleetApi.get("/drivers"), pausedDriverIds()])
      .then(([lists, dr, paused]) => {
        const shifts = (dr.data || []).filter((d) => d.status !== "retired" && !paused.has(d.id)).map((d) => d.shift_template || {});
        setOff(lists.flat().filter((t) => ids.has(t.id) && t.scheduled_pickup && !/cancel/i.test(t.status || "")).filter((t) => {
          const { wd, hm } = localParts(t.scheduled_pickup);
          return !shifts.some((s) => s[String(wd)] && s[String(wd)].start <= hm && hm < s[String(wd)].end);
        }).sort((a, b) => a.scheduled_pickup.localeCompare(b.scheduled_pickup)));
      }).catch(() => setOff([]));
  }, [tripIds, days]);

  if (!off?.length) return null;
  const early = off.filter((t) => localParts(t.scheduled_pickup).hm < "12:00").length;
  return (
    <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-amber-100" data-testid="import-shift-warning">
      <p className="flex items-center gap-2 text-sm font-semibold"><AlertTriangle className="h-4 w-4 text-amber-300" />
        {off.length} trip{off.length === 1 ? " starts" : "s start"} when no driver is on shift{early ? ` (${early} early morning)` : ""}
      </p>
      <p className="mt-1 text-xs text-amber-200/80">AI can't assign these until a driver's shift covers the pickup time.</p>
      <ul className="mt-2 space-y-0.5 text-xs" data-testid="import-shift-warning-list">
        {off.slice(0, 6).map((t) => (
          <li key={t.id} data-testid={`import-offshift-${t.id}`}>{fmtDayKey(dayKey(t.scheduled_pickup))} · {fmtTime(t.scheduled_pickup)} · {t.rider_name || t.external_trip_id}</li>
        ))}
        {off.length > 6 && <li className="text-amber-200/70">+{off.length - 6} more</li>}
      </ul>
      <Link to="/portal/shifts" data-testid="import-fix-shifts-link"
        className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-3 py-1.5 text-xs font-semibold text-slate-950 transition-colors hover:bg-amber-400">
        <CalendarClock className="h-3.5 w-3.5" /> Fix driver shifts
      </Link>
    </div>
  );
}
